import { Injectable } from '@angular/core';
import { ConfigService } from 'app/app-config.service';
import { UserInfoDto } from 'app/@types/dto/user-info-dto.model';

export enum OrganizationItemType {
    tag = 'tag',
    category = 'category',
}

@Injectable({
    providedIn: 'root',
})
export class UrlService {
    private baseUrl = 'http://localhost:30976/api';
    private clientName: string;

    constructor(private configService: ConfigService) {
        this.baseUrl = this.configService.getConfig.apiUrl;
        this.clientName = this.configService.getClient.name;
    }

    getRegister() {
        return `${this.baseUrl}/register`;
    }

    postRegister(clientName: string) {
        return `${this.baseUrl}/register/${clientName}`;
    }

    getUserOnboardingUrl() {
        return `${this.baseUrl}/onboarding/`;
    }

    getIndustriesUrl() {
        return `${this.baseUrl}/industries/`;
    }

    getUserClaimsUrl(organizationId?: string) {
        return organizationId ? `${this.baseUrl}/user/claims/${organizationId}` : `${this.baseUrl}/user/claims/`;
    }

    getSbti(organizationId: string) {
        return `${this.baseUrl}/plan/sbti/${organizationId}`;
    }

    getOrganizationUrl(organizationId: string | undefined = undefined): string {
        return organizationId ? `${this.baseUrl}/organization/${organizationId}` : `${this.baseUrl}/organization`;
    }

    getOrganizationZip(organizationId: string | undefined = undefined): string {
        return `${this.baseUrl}/export/questionnaire/${organizationId}`;
    }

    getAggregatedExcel(orgIds: string[]): string {
        return `${this.baseUrl}/export/organizations`;
    }

    getOrganizationEntityUrl(organizationId: string, organizationEntityId?: string): string {
        return organizationEntityId
            ? `${this.baseUrl}/entity/${organizationId}/${organizationEntityId}`
            : `${this.baseUrl}/entity/${organizationId}`;
    }

    getQuestionnaire(orgId: string, year: number, entityId: string) {
        const currentYear = year ? `?year=${year}` : '';
        const currentEntityId = entityId ? `&entityId=${entityId}` : '';
        return `${this.baseUrl}/questionnaire/${orgId}/${currentYear}${currentEntityId}`;
    }

    getAllQuestionnaires(orgId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/questionnaires`;
    }

    updateQuestionnaire(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionnaireUpdate/${orgId}`;
    }

    createQuestionnaire(orgId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}`;
    }

    createQuestionnaireFromPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/createFromPeriod/${orgId}`;
    }

    deleteQuestionnaireFromPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/deleteFromPeriod/${orgId}`;
    }

    editQuestionnairesPeriod(orgId: string) {
        return `${this.baseUrl}/questionnaire/editPeriods/${orgId}`;
    }

    deleteQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}`;
    }

    hardDeleteQuestionnaire(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/${orgId}/${questionnaireId}/hard`;
    }

    createUpdateQuestionInstance(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionInstance/${orgId}`;
    }

    deleteQuestionInstance(orgId: string) {
        return `${this.baseUrl}/questionnaire/questionInstance/${orgId}`;
    }

    getPricingPlanUrl(): string {
        return `${this.baseUrl}/pricingplans`;
    }

    getOrganizationPricingPlan(orgId: string) {
        return `${this.baseUrl}/pricingplan/organizationPricingPlan/${orgId}`;
    }

    updateOrganizationPricingPlan(orgId: string) {
        return `${this.baseUrl}/admin/organization/${orgId}/pricingplan`;
    }

    getPricingPlan(orgId: string) {
        return `${this.baseUrl}/pricingplan/${orgId}`;
    }

    getPricingPlansUrl(): string {
        return `${this.baseUrl}/pricingplans/${this.clientName}`;
    }

    getCockpitCompensateOfferRequestUrl(organizationId: string, organizationEntityId: string) {
        return `${this.baseUrl}/compensate_offer/${organizationId}/${organizationEntityId}`;
    }

    addNewUser() {
        return `${this.baseUrl}/user`;
    }

    updateUser() {
        return `${this.baseUrl}/user/create`;
    }

    deleteUser(userId: string) {
        return `${this.baseUrl}/user/${userId}`;
    }

    getAndUpdateRoles(organizationId: string) {
        return `${this.baseUrl}/roles/${organizationId}`;
    }

    deleteRole(organizationId: string, userId: string) {
        return `${this.baseUrl}/roles/${organizationId}/${userId}`;
    }

    createNewOrganization(): string {
        return `${this.baseUrl}/admin/organization`;
    }

    getOrUpdateOrganizationGlobalProperties(organizationId: string): string {
        return `${this.baseUrl}/admin/organization/${organizationId}`;
    }

    updateOrganization(orgId: string, isOnboarding: boolean) {
        return `${this.baseUrl}/organization/${orgId}/${isOnboarding}`;
    }

    getQuestionnaireExportUrl(organizationId: string, organizationEntityId: string, measureId: string) {}

    getQuestionnaireConfig(orgId: string, entityId: string): string {
        return this.clientName !== 'Dcarbonize'
            ? `${this.baseUrl}/qconfig/${orgId}/${entityId}?clientName=${this.clientName}`
            : `${this.baseUrl}/qconfig/${orgId}/${entityId}`;
    }

    getUsersList(): string {
        return `${this.baseUrl}/users`;
    }

    getUsersListByOrganization(...organizationIds: string[]) {
        return `${this.baseUrl}/user?organizationIds=${organizationIds}`;
    }

    getUser(userId: string): string {
        return `${this.baseUrl}/users/${userId}`;
    }

    getResults(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/questionnaire/result/${orgId}/${questionnaireId}`;
    }

    getBenchmark(sectorId: string, entityId: string) {
        return `${this.baseUrl}/benchmark/${sectorId}/${entityId}`;
    }

    getSolutions(orgId: string, questionnaireId: string) {
        return this.clientName !== 'Dcarbonize'
            ? `${this.baseUrl}/solution/${orgId}/${questionnaireId}?clientName=${this.clientName}`
            : `${this.baseUrl}/solution/${orgId}/${questionnaireId}`;
    }

    getComputeReductionUrl(orgId: string) {
        return `${this.baseUrl}/solution/reductionCo2/${orgId}`;
    }

    getCreateUpdateSolutionInstanceUrl(orgId: string) {
        return `${this.baseUrl}/solution/${orgId}`;
    }

    updateAllSolutionInstanceUrl(orgId: string, questionnaireId: string) {
        return `${this.baseUrl}/solution/all/${orgId}/${questionnaireId}`;
    }

    deleteSolutionInstance(orgId: string, questionnaireId: string, solutionTemplateCode: string) {
        return `${this.baseUrl}/solution/${orgId}/${questionnaireId}/${solutionTemplateCode}`;
    }

    createUpdateOrganisation(isUpdate: boolean, clientName: string, dedicatedAdminId?: string) {
        return isUpdate
            ? `${this.baseUrl}/admin/organization`
            : dedicatedAdminId
            ? `${this.baseUrl}/admin/organization/${clientName}/${dedicatedAdminId}`
            : `${this.baseUrl}/admin/organization/${clientName}`;
    }

    getOrganisations(adminId?: string) {
        return adminId ? `${this.baseUrl}/admin/organization/all/${adminId}` : `${this.baseUrl}/admin/organization/all`;
    }

    getAdminDashboardData(adminId?: string) {
        return adminId
            ? `${this.baseUrl}/admin/organization/dashboardData/${adminId}`
            : `${this.baseUrl}/admin/organization/dashboardData`;
    }

    getOrganizationsSolutions(adminId?: string) {
        return adminId
            ? `${this.baseUrl}/admin/organization/solutions/${adminId}`
            : `${this.baseUrl}/admin/organization/solutions`;
    }

    getEntities(adminId?: string) {
        return adminId
            ? `${this.baseUrl}/admin/organization/AllEntites/${adminId}`
            : `${this.baseUrl}/admin/organization/AllEntites`;
    }

    getUnassignedOrganisations() {
        return `${this.baseUrl}/admin/organization/unassigned`;
    }

    getFullOnboarding(orgId) {
        return `${this.baseUrl}/admin/organization/${orgId}`;
    }

    getDedicatedAdmins() {
        return `${this.baseUrl}/user/admin`;
    }

    userById(id: string) {
        return `${this.baseUrl}/user/${id}`;
    }

    createDedicatedAdmin() {
        return `${this.baseUrl}/user/create`;
    }
    getAvailableOrganization() {
        return `${this.baseUrl}/organization`;
    }

    getUserByOrganization(orgId: string) {
        return `${this.baseUrl}/user/users/${orgId}`;
    }

    addUserToScan() {
        return `${this.baseUrl}/user/addUserToScan`;
    }

    updateCognitoUser(user: UserInfoDto) {
        return `${this.baseUrl}/user/updateCognitoUserAttribute`;
    }

    removeUser(orgId: string, userId: string) {
        return `${this.baseUrl}/roles/${orgId}/${userId}`;
    }

    updateEntity(orgId: string) {
        return `${this.baseUrl}/roles/${orgId}`;
    }

    getCurrencies(orgId: string) {
        return `${this.baseUrl}/organization/currencies/${orgId}`;
    }

    getFileQuotaUrl(organizationId: string) {
        return `${this.baseUrl}/filequota/${organizationId}`;
    }

    buildSuffix(entries: Map<string, string | undefined>): string {
        let result = ``;

        let hasParameter = false;

        entries.forEach((val, key) => {
            if (val !== undefined && val !== '') {
                if (!hasParameter) {
                    result += `?${key}=${val}`;
                    hasParameter = true;
                } else {
                    result += `&${key}=${val}`;
                }
            }
        });

        return result;
    }
}
